/**
 * useAgGridColumns - Hook para definir las columnas de la tabla AG Grid de Pre-Facturas
 *
 * Alineado con InvoicesOrders: mismas columnas con valueGetters sobre datos raw (shipments).
 */

import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Product } from '@shared/components/ui/Product';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StorageDropdownActions } from '../components/interaction/StorageDropdownActions';
import { ViewPreInvoiceDropdownActions } from '../components/interaction/ViewPreInvoiceDropdownActions';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<any>[] => {
  return agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
    }));
};

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onEditPreInvoice?: (preInvoiceId: number) => void;
  onReturnPreInvoice?: (preInvoiceId: number) => void;
  onBlockPreInvoice?: (preInvoiceId: number) => void;
  onRemoveStorage?: (storageId: number) => void;
  showActions?: boolean;
  isStorageType?: boolean;
}

export const useAgGridColumns = ({
  selectedColumns = [],
  onEditPreInvoice,
  onReturnPreInvoice,
  onBlockPreInvoice,
  onRemoveStorage,
  showActions = true,
  isStorageType = false,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const currency = useCurrency();

  const allColumnDefs: ColDef[] = useMemo(() => {
    // 🎯 Columnas para storages
    if (isStorageType) {
      const storageColumns: CustomColDef[] = [
        {
          field: 'dateRange',
          headerName: t('table.date', 'Fecha'),
          minWidth: 200,
        },
        {
          field: 'product',
          headerName: t('table.product', 'Producto'),
          minWidth: 200,
          valueGetter: (params) => params.data?.product ?? '-',
          cellRenderer: (params: any) => {
            const productName = params.data?.product;
            const productCode = params.data?.productCode;
            const productId = params.data?.productId;

            // Si tenemos información del producto, usar el componente Product
            if (productName && productName !== '--' && productId) {
              return (
                <Product
                  id={productId}
                  name={productName}
                  src=""
                  code={productCode && productCode !== '--' ? productCode : undefined}
                  enableLineClamp={true}
                  showTooltip={true}
                />
              );
            }

            return <span className="text-sm">{productName || '-'}</span>;
          },
        },
        {
          field: 'quantity',
          headerName: t('table.quantity', 'Cantidad'),
          minWidth: 120,
        },
        {
          field: 'unitVolume',
          headerName: t('table.unit_volume', 'Volumen unitario'),
          minWidth: 150,
        },
        {
          field: 'totalVolume',
          headerName: t('table.total_volume', 'Volumen total'),
          minWidth: 150,
        },
        {
          field: 'dimensions',
          headerName: t('table.dimensions', 'Dimensiones'),
          minWidth: 180,
        },
        {
          field: 'baseAmount',
          headerName: t('table.base_amount', 'Importe base'),
          minWidth: 140,
          valueFormatter: (params) =>
            params.value == null ? '-' : formatCurrencyAmount(Number(params.value), currency),
        },
        {
          field: 'totalAmount',
          headerName: t('table.total_amount', 'Importe total'),
          minWidth: 150,
          valueFormatter: (params) =>
            params.value == null ? '-' : formatCurrencyAmount(Number(params.value), currency),
        },
        {
          field: 'linesCount',
          headerName: t('table.lines_count', 'Nº líneas'),
          minWidth: 120,
        },
      ];

      // 🎯 Agregar columna de acciones si showActions es true y hay handler
      if (showActions && onRemoveStorage) {
        storageColumns.push({
          field: 'actions',
          headerName: '',
          sortable: false,
          filter: false,
          width: 50,
          maxWidth: 50,
          minWidth: 50,
          pinned: 'right',
          lockPosition: 'right',
          suppressMovable: true,
          suppressSizeToFit: true,
          suppressAutoSize: true,
          cellRenderer: (params: any) => {
            const storage = params.data;

            // Si no hay storage o no tiene storageId, mostrar el dropdown de todas formas
            // pero el componente StorageDropdownActions manejará la visibilidad
            if (!storage) {
              return null;
            }

            // Handler para las acciones del dropdown
            const handleDropdownAction = (action: 'remove', storageId: number) => {
              if (action === 'remove' && onRemoveStorage) {
                onRemoveStorage(storageId);
              }
            };

            return (
              <div className="flex justify-center w-full">
                <StorageDropdownActions storage={storage} onAction={handleDropdownAction} />
              </div>
            );
          },
        });
      }

      return storageColumns;
    }

    // 🎯 Columnas para shipments (alineadas con InvoicesOrders - valueGetters sobre datos raw)
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.order', 'Pedido'),
        sortable: true,
        minWidth: 120,
        valueGetter: (params) => params.data?.order?.ref ?? '',
      },
      {
        field: 'order_id',
        headerName: t('table.order_id', 'ID Pedido'),
        sortable: true,
        minWidth: 100,
        valueGetter: (params) => params.data?.order_id ?? params.data?.order?.id ?? '',
      },
      {
        field: 'shop',
        headerName: t('table.shop', 'Tienda'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.order?.shop?.name ?? '',
      },
      {
        field: 'client',
        headerName: t('table.client', 'Cliente'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.order?.team?.name ?? '',
      },
      {
        field: 'payment_method',
        headerName: t('table.payment_method', 'Método de pago'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => (params.data?.order as any)?.payment_method_method ?? '',
      },
      {
        field: 'order_total',
        headerName: t('table.order_total', 'Total pedido'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => (params.data?.order as any)?.total ?? 0,
        cellRenderer: (params: any) => {
          const total = (params.data?.order as any)?.total ?? 0;
          return (
            <span className="text-sm font-medium">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(total)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'processed_at',
        headerName: t('table.shipment_date', 'Fecha de envío'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => {
          const shippingDate = params.data?.shipping_date;
          if (shippingDate && typeof shippingDate === 'object') {
            return shippingDate.date ?? '';
          }
          return shippingDate ?? '';
        },
        cellRenderer: (params: any) => {
          const shippingDate = params.data?.shipping_date;
          const dateStr = shippingDate?.date || shippingDate || null;
          return <span className="text-sm">{formatDate(dateStr)}</span>;
        },
      },
      {
        field: 'type',
        headerName: t('table.type', 'Tipo'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => params.data?.type ?? '',
        cellRenderer: (params: any) => {
          const type = params.data?.type;
          const typeTranslations: Record<string, string> = {
            SHIPPING: t('filters.typeOptions.shipping', 'Envío'),
            RETURN: t('filters.typeOptions.return', 'Devolución'),
            RESHIPMENT: t('filters.typeOptions.reshipment', 'Reenvío'),
          };
          return <span className="text-sm">{typeTranslations[type] || type || '-'}</span>;
        },
      },
      {
        field: 'internal_status',
        headerName: t('table.status', 'Estado'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.order?.status?.description_code ?? '',
      },
      {
        field: 'logistic_status',
        headerName: t('table.logistic_status', 'Estado logístico'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) =>
          params.data?.order?.shipment_status?.name ?? t('table.no_logistic_status', 'Sin estado logístico'),
      },
      {
        field: 'courier',
        headerName: t('table.courier', 'Courier'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.courier?.name ?? params.data?.order?.shipment?.courier?.name ?? '',
      },
      {
        field: 'country',
        headerName: t('table.country', 'País'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.country ?? '',
      },
      {
        field: 'city',
        headerName: t('table.city', 'Ciudad'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.city ?? '',
      },
      {
        field: 'zip',
        headerName: t('table.zip', 'CP'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.zip ?? '',
      },
      {
        field: 'num_items',
        headerName: t('table.num_items', 'N° Items'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.num_items ?? 0,
      },
      {
        field: 'weight_internal_kg',
        headerName: t('table.weight_internal_kg', 'Peso (kg)'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.weight_internal_kg ?? 0,
        cellRenderer: (params: any) => {
          const weight = params.data?.weight_internal_kg ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(weight)}{' '}
              kg
            </span>
          );
        },
      },
      {
        field: 'period',
        headerName: t('table.period', 'Período'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.period ?? '',
      },
      {
        field: 'shipping_base',
        headerName: t('table.shipping', 'Envío'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const shippingLine = params.data?.lines?.find((l: any) => l.concept === 'SHIPPING');
          return shippingLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const shippingLine = params.data?.lines?.find((l: any) => l.concept === 'SHIPPING');
          const amount = shippingLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'preparation_base',
        headerName: t('table.preparation', 'Preparación'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => {
          const preparationLine = params.data?.lines?.find((l: any) => l.concept === 'PREPARATION');
          return preparationLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const preparationLine = params.data?.lines?.find((l: any) => l.concept === 'PREPARATION');
          const amount = preparationLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'cod_base',
        headerName: t('table.cod', 'COD'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const codLine = params.data?.lines?.find((l: any) => l.concept === 'COD');
          return codLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const codLine = params.data?.lines?.find((l: any) => l.concept === 'COD');
          const amount = codLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'total',
        headerName: t('table.total', 'Total'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const lines = params.data?.lines || [];
          return lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);
        },
        cellRenderer: (params: any) => {
          const lines = params.data?.lines || [];
          const total = lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);
          return (
            <span className="text-sm font-medium">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(total)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'financial_status',
        headerName: t('table.financial_status', 'Estado Financiero'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data?.financial_status ?? '',
        cellRenderer: (params: any) => {
          const status = params.data?.financial_status;
          const statusTranslations: Record<string, string> = {
            NOT_INVOICED: t('filters.financialStatusOptions.notInvoiced', 'No facturado'),
            PREINVOICED: t('filters.financialStatusOptions.preinvoiced', 'Prefacturado'),
            INVOICED: t('filters.financialStatusOptions.invoiced', 'Facturado'),
            BLOCKED: t('filters.financialStatusOptions.blocked', 'Bloqueado'),
          };
          return <span className="text-sm">{statusTranslations[status] || status || '-'}</span>;
        },
      },
      {
        field: 'pre_invoice_id',
        headerName: t('table.pre_invoice_no', 'N° Pre-Factura'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.pre_invoice?.code ?? params.data?.pre_invoice_id ?? '',
      },
      {
        field: 'invoice_id',
        headerName: t('table.invoice_no', 'N° Factura'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) =>
          params.data?.invoice?.code ?? params.data?.invoice?.invoice_number ?? params.data?.invoice_id ?? '',
      },
      // 🎯 Columna de Acciones
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        hide: !showActions,
        cellRenderer: (params: any) => {
          const shipment = params.data;
          if (!shipment || !shipment.id) {
            return null;
          }

          const handleDropdownAction = (action: 'edit' | 'return' | 'block', shipmentId: number) => {
            if (action === 'edit' && onEditPreInvoice) {
              onEditPreInvoice(shipmentId);
            } else if (action === 'return' && onReturnPreInvoice) {
              onReturnPreInvoice(shipmentId);
            } else if (action === 'block' && onBlockPreInvoice) {
              onBlockPreInvoice(shipmentId);
            }
          };

          return (
            <div className="flex justify-center w-full">
              <ViewPreInvoiceDropdownActions preInvoiceId={shipment.id} onAction={handleDropdownAction} />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [
    t,
    currency,
    onEditPreInvoice,
    onReturnPreInvoice,
    onBlockPreInvoice,
    onRemoveStorage,
    showActions,
    isStorageType,
  ]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    return allColumnDefs.filter((col) => {
      const field = col.field || '';
      if (field === 'actions') {
        return showActions;
      }

      return selectedColumns.length === 0 || selectedColumns.includes(field);
    });
  }, [allColumnDefs, selectedColumns, showActions]);

  return {
    allColumnDefs: filteredColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
